/* Loading Google Identity Services, once.

   The button has to be drawn by Google's own script — the credential it hands back is a signed
   token only Google can mint, so there is nothing to reimplement and no way to fake the flow with
   our own markup. The script is loaded on demand rather than from `public/index.html` because it is
   needed by two modals and by nobody who never opens one: a player who arrives already signed in
   should not be fetching accounts.google.com to look at the map.

   Loading is idempotent. `google.accounts.id.initialize` is global state — calling it twice with
   different client IDs is a configuration nobody can reason about — so the promise is cached and
   every caller after the first gets the same resolved namespace. */

export const GOOGLE_IDENTITY_SRC = "https://accounts.google.com/gsi/client"

/* A script request that stalls fires neither `load` nor `error`, so without this the promise below
   is pending for ever and the button's own `.catch` — the one that says "Google sign-in could not
   be loaded" — never runs. What the player sees then is an "or" divider above an empty box, which
   reads as a button still loading and never resolves into anything. A captive portal, a proxy that
   black-holes the host and a phone that lost signal mid-request all land here; a blocked request,
   which is the common case, still fails fast through `error`. */
export const GOOGLE_IDENTITY_TIMEOUT_MS = 8000

let pending = null

const namespace = () => window.google?.accounts?.id

export const loadGoogleIdentity = () => {
  if (namespace()) return Promise.resolve(namespace())
  if (pending) return pending

  pending = new Promise((resolve, reject) => {
    // A tag already in the page without the namespace beside it is a load that failed, not one to
    // wait on: its `load` event has been and gone, so listening to it would hang for ever. Drop it
    // and start again.
    document.querySelectorAll(`script[src="${GOOGLE_IDENTITY_SRC}"]`).forEach((tag) => tag.remove())

    const script = document.createElement("script")
    let timer = null
    const giveUp = (message) => {
      // Cleared so a later attempt can retry. An ad blocker or an offline moment is not permanent,
      // and a cached rejected promise would keep the button broken until a reload.
      pending = null
      clearTimeout(timer)
      script.remove()
      reject(new Error(message))
    }

    timer = setTimeout(() => giveUp("Google Identity Services did not answer"), GOOGLE_IDENTITY_TIMEOUT_MS)

    script.addEventListener("load", () => {
      clearTimeout(timer)
      if (namespace()) {
        resolve(namespace())
      } else {
        // Loaded, and the namespace is not there — a blocked or rewritten response. Failing beats
        // resolving with undefined and letting the caller crash on `.initialize`.
        giveUp("Google Identity Services loaded without its namespace")
      }
    })
    script.addEventListener("error", () => giveUp("Google Identity Services could not be loaded"))

    script.src = GOOGLE_IDENTITY_SRC
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })

  return pending
}

// Tests mount and unmount the button; without this the module-level cache leaks between them.
export const resetGoogleIdentityForTests = () => {
  pending = null
}

export default loadGoogleIdentity
